import { forwardRef, useCallback, useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useTranslation } from "react-i18next";

/**
 * O ref é repassado ao componente embrulhado para que wrappers do MUI que
 * precisam medir o gatilho — `Tooltip`, `Popper` — consigam ancorar no botão.
 * Sem `forwardRef`, o React 18 descarta o ref com um warning e o tooltip
 * aparece desalinhado.
 */
const withConfirmation = (WrappedComponent) =>
  forwardRef(function WithConfirmation(
    {
      title,
      dialogTitle,
      description,
      confirmText,
      cancelText,
      onConfirm,
      children,
      onClick,
      validate,
      dialogProps = {},
      ...props
    },
    ref,
  ) {
    const [open, setOpen] = useState(false);
    const { t } = useTranslation();
    const confirmationLabels = t("frontend.labels.confirmation");
    const actionLabels = t("frontend.labels.actions");

    /**
     * `dialogTitle` existe porque `title` significa duas coisas quando este
     * botão é embrulhado por um `Tooltip`: aqui é o cabeçalho do diálogo, e
     * para o MUI é o texto do balão. Ancorado direto no botão, o Tooltip
     * reclama de `children.props.title` a cada render — e, se o chamador
     * omitisse `title`, o MUI injetaria o texto do balão e ele viraria o
     * cabeçalho do diálogo. Quem passa `dialogTitle` fica imune aos dois:
     * o `title` injetado é consumido aqui e nunca chega ao DOM.
     */
    const defaultTitle = dialogTitle || title || confirmationLabels.title;
    const defaultDescription = description || confirmationLabels.are_you_sure;
    const defaultConfirmText = confirmText || actionLabels.confirm;
    const defaultCancelText = cancelText || actionLabels.cancel;

    /**
     * `validate` deixa o chamador vetar a abertura da confirmação.
     *
     * Sem isso, um botão que confirma o submit de um formulário mostra a
     * pergunta antes de o formulário ser validado: o usuário lê uma descrição
     * montada com dados que ainda não escolheu, confirma, e nada acontece —
     * porque o `handleSubmit` só então barra. Validando antes, o erro aparece
     * no campo e a confirmação só é feita quando há o que confirmar.
     */
    const handleOpen = async (e) => {
      onClick?.(e);
      if (validate && !(await validate())) return;
      setOpen(true);
    };
    const handleClose = () => setOpen(false);

    const handleConfirm = useCallback(() => {
      onConfirm();
      handleClose();
    }, [onConfirm]);

    return (
      <>
        <WrappedComponent ref={ref} onClick={handleOpen} {...props}>
          {children}
        </WrappedComponent>

        <Dialog
          open={open}
          onClose={handleClose}
          maxWidth="xs"
          fullWidth
          {...dialogProps.wrapper}
        >
          <DialogTitle
            sx={{ color: "black", fontWeight: 500 }}
            {...dialogProps.title}
          >
            {defaultTitle}
          </DialogTitle>

          <DialogContent dividers {...dialogProps.content}>
            <DialogContentText
              sx={{ my: 1, color: "black" }}
              {...dialogProps.contentText}
            >
              {defaultDescription}
            </DialogContentText>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }} {...dialogProps.actions}>
            <Button
              onClick={handleClose}
              variant="light-outlined"
              {...dialogProps.cancelButton}
            >
              {defaultCancelText}
            </Button>
            <Button
              onClick={handleConfirm}
              variant="light"
              autoFocus
              {...dialogProps.confirmButton}
            >
              {defaultConfirmText}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  });

export default withConfirmation;
